import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GTM_EVENTS } from '../constants/widget-constants';
import { CustomFieldFileUploadModel, CustomFieldValueModel, CustomFieldsDao } from '../db-models/appointment-custom-fields-dao';
import { AppointmentReservationBody, AppointmentReservationResponse } from '../db-models/appointment-reservation.model';
import { AppointmentServiceDao } from '../db-models/appointment-service-dao';
import { CouponDbModel } from '../db-models/coupon-db.model';
import { EventDao } from '../db-models/event-dao';
import { EventDateDao } from '../db-models/event-date-dao';
import { DayWiseFreeSlotsDao } from '../db-models/free-appointment-dao';
import { StoresDao } from '../db-models/stores-dao';
import { UserDao } from '../db-models/user-data-dao';
import { ZipCodeItem, ZipCodeResponse } from '../db-models/zip-code.model';
import { WidgetAppointmentGroupModel } from '../db-models/widget-appointment-group.dao';
import { EventBookingModel } from '../models/booking.model';
import { AppointmentBookItem, EventBookItem } from '../models/cart.model';
import { MeetingTypeModel } from '../models/meeting-types.model';
import { resendPaidBookingWhileLocked } from './booking-outcome.util';
import { GoogleAnalyticsService } from './google-analytics.service';
import { HelperService } from './helper.service';
import { PartnerService } from './partner.service';

@Injectable({
  providedIn: 'root',
})
export class FormsService {
  constructor(
    public http: HttpClient,
    private googleAnalyticsService: GoogleAnalyticsService,
    private helperService: HelperService,
    private partnerService: PartnerService
  ) { }

  imageUploaded = true;
  personalFormValid = false;

  downloadAppointmentByUUID(uuid: string): Observable<{ file: string; type: string }> {
    return this.http.get<{ file: string; type: string }>(`${environment.apiUrl}appointments/download/${uuid}`);
  }

  downloadEventByUUID(uuid: string): Observable<{ file: string; type: string }> {
    return this.http.get<{ file: string; type: string }>(`${environment.apiUrl}customers_slots/download/${uuid}`);
  }

  getStores(body: { appointment_service_ids: number | number[], worker_id?: number, worker_ids?: number[], category_ids?: number[], selected_store_id?: number }): Observable<StoresDao[]> {
    return this.http.post<StoresDao[]>(environment.apiUrl + 'stores/stores_with_workers', body).pipe(
      switchMap(stores => {
        let finalStores = [];
        if (stores?.length) {
          finalStores = stores.map(store => {
            return {
              postcode_sore_id: `${store.id}`,
              ...store
            }
          });
        }
        return of(finalStores?.length ? this.helperService.sortByNumber(finalStores, 'position') : []);
      })
    );
  }

  getStoresByPostCode(postcode: string, sort = true): Observable<StoresDao[]> {
    return this.http.post<StoresDao[]>(environment.apiUrl + 'stores/store_by_postcode', { postcode }).pipe(
      switchMap(stores => {
        if (stores?.length) {
          const finalStores = stores.map(store => {
            if (store?.store) {
              return {
                postcode_details: {
                  id: store.id,
                  city: store.city,
                  postcode: store.postcode
                },
                postcode_sore_id: `${store.store.id}-${store.id}`,
                ...store.store
              }
            } else {
              return {
                postcode_sore_id: `${store.id}`,
                ...store
              }
            }

            return store;
          });


          if (finalStores?.length) {
            return sort ? of(this.helperService.sortByNumber(finalStores, 'position')) : of(finalStores);
          }
        }

        return of([]);
      })
    );
  }

  getCitiesByZip(zip: string, limit = 15, offset = 0): Observable<ZipCodeItem[]> {
    const params = new HttpParams()
      .set('zip', zip)
      .set('limit', limit.toString())
      .set('offset', offset.toString());
    return this.http.get<ZipCodeResponse>(`${environment.apiUrl}zip_codes`, { params }).pipe(
      map((response: ZipCodeResponse) => response?.zip_code?.zips || []),
      catchError(() => of([]))
    );
  }

  getAppointmentServices(payload: { store_id: number, worker_id?: number, worker_ids?: number[], appointment_services_uuids?: string[], postcode?: string }): Observable<AppointmentServiceDao[]> {
    const body = payload;
    return this.http.post<AppointmentServiceDao[]>(`${environment.apiUrl}appointment_services/appointment_services_by_store`, body);
  }

  getInactiveWeekDays(workerId: number, storeId: number, appointmentIds: string): Observable<number[]> {
    const body = {
      worker_id: workerId,
      appointment_ids: appointmentIds,
      store_id: storeId,
    };
    return this.http.post<number[]>(environment.apiUrl + 'availabilities/inactive_week_days', body);
  }

  getDaysWiseFreeSlots(
    workerId: number | number[],
    storeId: number,
    appointmentIds: string,
    date: string,
    isInternal: boolean,
    conferenceUuid = undefined,
    debug = undefined,
    internalToken: string,
    meetingTypeId = undefined,
    exceptionalWorker = undefined,
    postcode = undefined,
    timezone = undefined
  ): Observable<DayWiseFreeSlotsDao> {
    let body: Params;
    if (conferenceUuid) {
      body = {
        start: date,
        booking_link_uuid: conferenceUuid,
        is_internal: isInternal,
        ...timezone && { timezone }
      };
    } else {
      body = {
        worker_id: workerId,
        appointment_ids: appointmentIds,
        store_id: storeId,
        start: date,
        is_internal: isInternal,
        ...meetingTypeId && { meeting_type_id: meetingTypeId },
        ...exceptionalWorker?.length && { worker_exceptions: exceptionalWorker },
        ...postcode && { postcode },
        ...timezone && { timezone }
      };
    }

    // Round Robin V2 channel scope — always sent; the BE only honours it when the RR-V2
    // feature + setting are enabled (it fails closed otherwise). Internal/admin wins; a
    // conference booking-link or the booking-page host both map to booking_link; else
    // it's a standalone widget embed.
    if (isInternal) {
      body.channel = 'internal_widget';
    } else if (conferenceUuid || this.partnerService.isFromBookingPage) {
      body.channel = 'booking_link';
    } else {
      body.channel = 'booking_widget';
    }

    // The BE supports an array worker_id only on booking_widget/booking_link —
    // on other channels fall back to anyone (0).
    if (Array.isArray(body.worker_id) && !['booking_widget', 'booking_link'].includes(body.channel)) {
      body.worker_id = 0;
    }

    if (debug) {
      body.debug = debug;
      body.token = internalToken;
    }

    return this.http.post<DayWiseFreeSlotsDao>(`${environment.apiUrl}availabilities/free_appointments`, body);
  }

  getPartnerQualificationQuestions(): Observable<CustomFieldsDao[]> {
    return this.http.get<CustomFieldsDao[]>(environment.apiUrl + 'custom_fields/qualification_based_custom_fields');
  }

  getAppointmentCustomFields(servicesId: number[]): Observable<CustomFieldsDao[]> {
    const body = { appointment_service_ids: servicesId };
    return this.http.post<CustomFieldsDao[]>(environment.apiUrl + 'custom_fields/custom_fields_by_appointment_service_ids', body);
  }

  bookAppointments(bookingItems: AppointmentBookItem[]): Observable<{
    success: boolean;
    errors: { message: string }[];
    appointment: any;
  }> {
    // preparing gtm params for gtm event
    const gtmParams = { appointments: [] };
    bookingItems.forEach(bookingItem => {
      let appointmentServiceName = '';
      if (bookingItem?.referenceServices?.length > 0) {
        appointmentServiceName = bookingItem?.referenceServices
          .map((item) => item.serviceName)
          .join(',');
      }
      if (!bookingItem?.timezone) {
        delete bookingItem?.timezone;
      }

      gtmParams.appointments.push({
        appointment_service_id: bookingItem.services,
        appointment_service_name: appointmentServiceName,
        store_id: bookingItem.store_id,
        store_name: bookingItem.store_name,
        worker_id: bookingItem.worker_id,
        resource_name: bookingItem.resource_name,
        worker_email: bookingItem.worker_email
      });
    });

    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.APPOINTMENT_BOOKING, gtmParams);

    return this.http.post<{
      success: boolean;
      errors: { message: string }[];
      appointment: any;
    }>(environment.apiUrl + 'appointments/book', bookingItems)
      .pipe(resendPaidBookingWhileLocked(bookingItems.some(item => !!item?.adyen_session_id)));
  }

  getEventCustomFields(eventUuid: string | number): Observable<CustomFieldsDao[]> {
    const body: { event_uuid: string | number } = { event_uuid: eventUuid };
    return this.http.post<CustomFieldsDao[]>(
      environment.apiUrl + 'custom_fields/custom_fields_by_event',
      body
    );
  }

  getCustomFieldValues(customerUuid: number): Observable<CustomFieldValueModel[]> {
    const body = { customer_uuid: customerUuid };
    return this.http.post<CustomFieldValueModel[]>(environment.apiUrl + 'customer_custom_fields/customer', body);
  }

  getEvents(limit: number, offset: number, store_uuids: string[], category_uuids: string[]): Observable<{ events: EventDao[], total_count: number, has_secret: boolean }> {
    const body = {
      status: { value: 1, operator: '=' },
      store_uuids: store_uuids,
      category_uuids: category_uuids
    };

    const params = new HttpParams()
      .set('limit', limit)
      .set('offset', offset);
    return this.http.post<{ events: EventDao[], total_count: number, has_secret: boolean }>(environment.apiUrl + 'events/filter', body, { params });
  }

  getEventByIdOrUuid(uuid: number | string): Observable<EventDao> {
    return this.http.get<{ event: EventDao }>(`${environment.apiUrl}events/${uuid}`).pipe(
      map(eventData => eventData?.event)
    );
  }

  getEventSlotsByEventIdAndDate(
    eventUuid: string | number,
    date: string,
    timezone: string
  ): Observable<{ slotDates: (string | number)[]; slotsCount: number }> {
    const body = { event_uuid: eventUuid, date: date, timezone };
    return this.http.post<{ slotDates: string[]; slotsCount: number }>(environment.apiUrl + 'events/slot_dates', body);
  }

  bookEvents(bookingItems: EventBookItem[]): Observable<{
    success: boolean;
    notificationHelperArray: EventBookingModel[];
    errors: { message: string }[];
    bookingErrors?: { code: number; message: string }[];
  }> {
    // preparing gtm params for gtm events
    const gtmParams = { events: [] };
    bookingItems.forEach(bookingItem => {
      gtmParams.events.push({
        event_uuid: bookingItem.event_id,
        event_category: bookingItem.category,
        event_title: bookingItem?.event_name || null,
        worker_id: bookingItem?.worker_id || null
      });
    });
    this.googleAnalyticsService.emitBookingEvent(GTM_EVENTS.EVENT_BOOKING, gtmParams);

    return this.http.post<{
      success: boolean;
      notificationHelperArray: EventBookingModel[];
      errors: { message: string }[];
      bookingErrors?: { code: number; message: string }[];
    }>(environment.apiUrl + 'events/book', bookingItems)
      .pipe(resendPaidBookingWhileLocked(bookingItems.some(item => !!item?.adyen_session_id)));
  }

  getMeetingTypesByAppointmentServiceIds(
    worker_id: number, selected_appointment_service_ids: number[], booking_link_uuid?: string, worker_ids: number[] = []
  ): Observable<MeetingTypeModel[]> {
    const body: {
      worker_id?: number;
      selected_appointment_service_ids?: number[];
      booking_link_uuid?: string;
      worker_ids?: number[];
    } = {};

    if (booking_link_uuid) {
      body.booking_link_uuid = booking_link_uuid;
    } else {
      worker_id && (body.worker_id = worker_id);
      worker_ids?.length && (body.worker_ids = worker_ids);
    }
    // The services are sent in both cases: the backend resolves the workers from the booking link,
    // but only the client knows which of the link's services was picked, and the per-service
    // meeting-type restriction is applied against this list.
    selected_appointment_service_ids && (body.selected_appointment_service_ids = selected_appointment_service_ids);

    return this.http.post<MeetingTypeModel[]>(environment.apiUrl + 'meeting_types/list', body).pipe(
      switchMap(meetingTypes => {
        if (Array.isArray(meetingTypes)) {
          return of(meetingTypes?.length ? meetingTypes : []);
        }
        return of(meetingTypes ? Object.values(meetingTypes) as MeetingTypeModel[] : []);
      })
    );
  }

  getMeetingTypesBySlotWorkerId(event_uuid: string | number): Observable<MeetingTypeModel[]> {
    return this.http.get<MeetingTypeModel[]>(environment.apiUrl + 'meeting_types/event_list/' + event_uuid).pipe(
      switchMap(meetingTypes => {
        if (Array.isArray(meetingTypes)) {
          return of(meetingTypes?.length ? meetingTypes : []);
        }
        return of(meetingTypes ? Object.values(meetingTypes) as MeetingTypeModel[] : []);
      })
    );
  }

  getSlots(eventUuid: string | number, date: string, mode: 'day' | 'year' = 'day', timezone: string): Observable<EventDateDao[]> {
    const body = {
      event_uuid: eventUuid,
      date: date,
      mode: mode,
      ...timezone && { timezone }
    };
    return this.http.post<{ slotsByDate: EventDateDao[] }>(`${environment.apiUrl}events/slots`, body).pipe(
      map(dates => dates.slotsByDate)
    );
  }

  applyCoupon(couponPayload: { coupon: string, email?: string, amount?: number }): Observable<CouponDbModel> {
    return this.http.post<CouponDbModel>(`${environment.apiUrl}coupons/verify`, couponPayload);
  }

  getFilesByFileIds(fileIds: number[]): Observable<CustomFieldFileUploadModel[]> {
    const body = { ids: fileIds };
    return this.http.post<CustomFieldFileUploadModel[]>(environment.apiUrl + 'files/get_files_by_ids', body);
  }

  uploadCustomFieldFiles(fileList: FileList, field: CustomFieldsDao): Observable<
    CustomFieldFileUploadModel[] | { errors: { code: string, message: string } }
  > {
    const formData = new FormData();
    for (let i = 0; i < fileList.length; i++) {
      formData.append('file[]', fileList[i]);
    }
    formData.append('custom_field_id', field.id.toString());
    return this.http.post<CustomFieldFileUploadModel[]>(environment.apiUrl + 'files', formData);
  }

  deleteFile(file: CustomFieldFileUploadModel): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(environment.apiUrl + 'files/' + file.uuid);
  }

  getStripeAccountId(): Observable<{ stripeEnabled: boolean; accountId: string; }> {
    return this.http.get<{ stripeEnabled: boolean; accountId: string }>(environment.apiUrl + 'stripe/verify');
  }

  getWidgetAppointmentGroups(): Observable<WidgetAppointmentGroupModel[]> {
    return this.http.get<WidgetAppointmentGroupModel[]>(`${environment.apiUrl}widget_appointment_groups`).pipe(
      map(groups => this.normalizeWidgetGroupTimes(groups))
    );
  }

  getStoreWiseWidgetAppointmentGroups(store_id: number): Observable<WidgetAppointmentGroupModel[]> {
    const body = { store_id };
    return this.http.post<WidgetAppointmentGroupModel[]>(`${environment.apiUrl}widget_appointment_groups/by_store`, body).pipe(
      map(groups => this.normalizeWidgetGroupTimes(groups))
    );
  }

  // The API serializes the groups' start/end time columns as bare "HH:mm:ss"
  // strings (full ISO datetimes before CakePHP 4); prefix a dummy date so
  // consumers can keep treating them as datetimes.
  private normalizeWidgetGroupTimes(groups: WidgetAppointmentGroupModel[]): WidgetAppointmentGroupModel[] {
    if (!Array.isArray(groups)) {
      return groups;
    }

    const toDateTime = (value: Date | string): Date | string =>
      typeof value === 'string' && !value.includes('T') ? `2000-01-01T${value}` : value;

    return groups.map(group => ({
      ...group,
      start: toDateTime(group.start),
      end: toDateTime(group.end)
    }));
  }

  verifyPhoneNumber(mobile: string, type: string): Observable<{ verified: boolean; count: number }> {
    const body = { mobile, type };
    return this.http.post<{ verified: boolean; count: number }>(environment.apiUrl + 'partners/verify_mobile_number', body);
  }

  requestSmsCode(mobile: string, type: string): Observable<{ requested: boolean; count: number }> {
    const body = { mobile, type };
    return this.http.post<{ requested: boolean; count: number }>(environment.apiUrl + 'partners/request_sms_code', body);
  }

  confirmSmsCode(mobile: string, verification_code: number, type: string): Observable<{ verified: boolean; count: number }> {
    const body = { mobile, verification_code, type };
    return this.http.post<{ verified: boolean; count: number }>(environment.apiUrl + 'partners/confirm_sms_code', body);
  }

  filterWorkers(body: {
    auth0_user_id?: { value: string; operator: string; };
    is_lead_generator?: { value: string; operator: string; };
  }): Observable<UserDao[]> {
    return this.http.post<UserDao[]>(`${environment.apiUrl}workers/filter`, body).pipe(
      switchMap(workers => {
        return of(workers || []);
      })
    );
  }

  getCustomFieldsByIds(ids: number[]): Observable<CustomFieldsDao[]> {
    const body = { ids };
    return this.http.post<CustomFieldsDao[]>(`${environment.apiUrl}custom_fields/get_by_ids`, body).pipe(
      switchMap(customFields => {
        customFields?.forEach(customField => {
          customField.position = Number(customField.position);
        });

        return of(customFields);
      })
    );
  }

  reserveAppointments(appointments: AppointmentReservationBody[]): Observable<AppointmentReservationResponse> {
    return this.http.post<AppointmentReservationResponse>(`${environment.apiUrl}appointment_reservations`, appointments, {})
      .pipe(
        switchMap(reservation => {
          return of({ success: reservation?.success ?? false, preReserved: false });
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 403) {
            return of({ success: false, preReserved: true, reservedAppointments: error.error.alreadyReserved });
          } else {
            return throwError(() => error);
          }
        }),
      );
  }

  removeReservedAppointment(appointment: AppointmentReservationBody[]): Observable<{ success: boolean; }> {
    return this.http.delete<{ success: boolean; }>(`${environment.apiUrl}appointment_reservations`, { body: appointment });
  }
}
